<script lang="ts">
	import type { ServiceEquipProps } from "./ServiceEquipProps.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import pallets from "../assets/images/pallets.png?webp&w=360&imagetools";
	import dynamicSystems from "../assets/images/dynamic-systems.png?webp&w=360&imagetools";
	import crates from "../assets/images/crates.png?webp&w=360&imagetools";
	import equip from "../assets/images/equip.jpg?webp&w=360&imagetools";
	import ServicePage from "../components/ServicePage.svelte";
	import equip1 from "../assets/images/vybavime-sklad-1.jpg?webp&w=1920&imagetools";
	import equip2 from "../assets/images/vybavime-sklad-2.jpg?webp&w=1920&imagetools";
	import equip3 from "../assets/images/vybavime-sklad-3.jpg?webp&w=1920&imagetools";
	import equip4 from "../assets/images/vybavime-sklad-4.jpg?webp&w=1920&imagetools";

	type $$Props = ServiceEquipProps;

	const { references, blogPosts } = $$restProps as $$Props;

	const images = [equip1, equip2, equip3, equip4];

	const heading = "Vybavíme váš sklad \n podle potřeby";
	const title =
		"Společnost BITO skladovací technika \n nabízí komplexní sortiment inovativních řešení \n pro skladování a logistiku.";
	const perex =
		"Naše produkty zahrnují regálové systémy, dynamické skladování, plastové přepravky \n a kompletní automatizovaná řešení, která zvyšují efektivitu skladování a manipulace.";
	const perex2 =
		"Díky našim více než 50letým zkušenostem a individuálnímu přístupu k zákazníkům pomáháme optimalizovat prostor \n i procesy ve skladech různých velikostí a průmyslových odvětví. Klademe důraz na kvalitu, spolehlivost \n a udržitelnost našich produktů, abychom zajistili dlouhodobé řešení vašich logistických potřeb.";
</script>

<ServicePage {heading} {title} {perex} {perex2} {images} {references} {blogPosts}>
	<div class="mb-32 flex flex-col items-center gap-14">
		<p class="text-center text-lg font-bold text-gray-400 lg:text-xl">
			{nbspify("Jaké systémy")}
			<br />
			{nbspify("a řešení nabízíme?")}
		</p>
		<div class="grid grid-cols-1 gap-y-5 px-4 md:grid-cols-2 xl:grid-cols-4">
			<div class="w-[17rem]">
				<span class="font-quicksand group-hover:text-tertiary text-gray text-[4.375rem] leading-[3.3rem]">1</span>
				<div class="mt-4 flex flex-col">
					<StaticPicture
						image={pallets}
						width={272}
						height={272}
						alt="Regálové systémy"
						imgClass="object-cover h-[17rem]"
					/>
					<div class="border-gray flex h-[16rem] flex-col gap-4 border p-6 md:border-r-0">
						<p class="text-primary text-md font-extrabold">Regálové <br /> systémy</p>
						<ul class="text-bito-black ml-6 list-disc tracking-[.005em]">
							<li>{nbspify("Paletové regály")}</li>
							<li>{nbspify("Policové regály")}</li>
							<li>{nbspify("Konzolové regály")}</li>
							<li>{nbspify("Mezaniny a plošiny")}</li>
						</ul>
					</div>
				</div>
			</div>
			<div class="w-[17rem]">
				<span class="font-quicksand group-hover:text-tertiary text-gray text-[4.375rem] leading-[3.3rem]">2</span>
				<div class="mt-4 flex flex-col">
					<StaticPicture
						image={equip}
						width={272}
						height={272}
						alt="Dynamické skladovací systémy"
						imgClass="object-cover h-[17rem]"
					/>
					<div class="border-gray flex h-[16rem] flex-col gap-4 border p-6 xl:border-r-0">
						<p class="text-primary text-md font-extrabold">{nbspify("Dynamické skladovací systémy")}</p>
						<ul class="text-bito-black ml-6 list-disc tracking-[.005em]">
							<li>Průtokové regály</li>
							<li>Regály pro spádové skladování</li>
							<li>Systémy pro FIFO <br /> (First In, First Out) <br /> a LIFO (Last In, First Out)</li>
						</ul>
					</div>
				</div>
			</div>
			<div class="w-[17rem]">
				<span class="font-quicksand group-hover:text-tertiary text-gray text-[4.375rem] leading-[3.3rem]">3</span>
				<div class="mt-4 flex flex-col">
					<StaticPicture
						image={crates}
						width={272}
						height={272}
						alt="Plastové přepravky"
						imgClass="h-[17rem] object-cover"
					/>
					<div class="border-gray flex h-[16rem] flex-col gap-4 border p-6 md:border-r-0">
						<p class="text-primary text-md font-extrabold">Plastové přepravky <br /> a kontejnery</p>
						<ul class="text-bito-black ml-6 list-disc tracking-[.005em]">
							<li>{nbspify("Stohovatelné přepravky")}</li>
							<li>{nbspify("Euro přepravky")}</li>
							<li>{nbspify("Skládací přepravky")}</li>
							<li>{nbspify("Speciální přepravky pro automatizované systémy")}</li>
						</ul>
					</div>
				</div>
			</div>
			<div class="w-[17rem]">
				<span class="font-quicksand group-hover:text-tertiary text-gray text-[4.375rem] leading-[3.3rem]">4</span>
				<div class="mt-4 flex flex-col">
					<StaticPicture
						image={dynamicSystems}
						width={272}
						height={272}
						alt="Automatizované řešení"
						imgClass="object-cover h-[17rem]"
					/>
					<div class="border-gray flex h-[16rem] flex-col gap-4 border p-6">
						<p class="text-primary text-md font-extrabold">Automatizovaná <br /> skladovací řešení</p>
						<ul class="text-bito-black ml-6 list-disc tracking-[.005em]">
							<li>{nbspify("Automatizované skladovací a vyhledávací systémy (ASRS)")}</li>
							<li>{nbspify("Shuttle systémy")}</li>
							<li>{nbspify("Robotická řešení pro skladování a manipulaci")}</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	</div>
</ServicePage>
